import type { SouscriptionEntry } from '../types';
import { MONTH_ABBR } from './constants';

/**
 * Tableau croisé compagnie × mois - grille de la page KPI consolidés.
 *
 * Cf SPEC-PORT.md §7.3.C :
 *  - Groupement compagnie × mois (YYYY-MM) basé sur `dateField` (toggle UI)
 *  - Colonnes mois triées chronologiquement ASC
 *  - Lignes triées par total DESC
 *  - Labels mois au format "MMM yy" en FR (ex: "avr. 25")
 */

export interface CrossTableRow {
  company: string;
  total: number;
  byMonth: Record<string, number>; // clé YYYY-MM
}

export interface CrossTable {
  months: Array<{ key: string; label: string }>; // ordonnés ASC
  rows: CrossTableRow[]; // triés par total DESC
  monthTotals: Record<string, number>;
  grandTotal: number;
}

export function buildCrossTable(
  entries: SouscriptionEntry[],
  dateField: 'dateSouscription' | 'dateEffet' = 'dateSouscription',
): CrossTable {
  const byCompanyMonth = new Map<string, Map<string, number>>();
  const monthSet = new Set<string>();

  for (const e of entries) {
    const date = e[dateField];
    if (!date) continue;
    const mk = date.substring(0, 7); // YYYY-MM
    monthSet.add(mk);
    const companyMap = byCompanyMonth.get(e.company) ?? new Map<string, number>();
    companyMap.set(mk, (companyMap.get(mk) ?? 0) + 1);
    byCompanyMonth.set(e.company, companyMap);
  }

  const months = Array.from(monthSet)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => {
      const [y, m] = key.split('-').map(Number);
      // Format "avr. 25" — MONTH_ABBR (3 lettres) + YY (2 derniers chiffres) minuscule.
      // Le spec utilise date-fns `format(parseISO(...), 'MMM yy', { locale: fr })`
      // qui donne "avr. 25". On reproduit ça en dur pour ne pas dépendre du
      // format locale-dependent (qui peut varier selon version date-fns).
      const mmm = MONTH_ABBR[(m ?? 1) - 1]!.toLowerCase();
      const mmWithDot = mmm.length <= 4 ? `${mmm}.` : mmm; // juin, août n'ont pas de point
      const yy = String((y ?? 0) % 100).padStart(2, '0');
      return { key, label: `${mmWithDot} ${yy}` };
    });

  const rows: CrossTableRow[] = [];
  for (const [company, map] of byCompanyMonth) {
    const byMonth: Record<string, number> = {};
    let total = 0;
    for (const [mk, count] of map) {
      byMonth[mk] = count;
      total += count;
    }
    rows.push({ company, total, byMonth });
  }
  rows.sort((a, b) => b.total - a.total);

  const monthTotals: Record<string, number> = {};
  let grandTotal = 0;
  for (const m of months) monthTotals[m.key] = 0;
  for (const r of rows) {
    for (const m of months) {
      const v = r.byMonth[m.key] ?? 0;
      monthTotals[m.key] = (monthTotals[m.key] ?? 0) + v;
      grandTotal += v;
    }
  }

  return { months, rows, monthTotals, grandTotal };
}
